import React from 'react';
import PropTypes from 'prop-types';
import {
    WeezHeader,
    OptionalSection,
    Icon,
    FrameCard,
    Tooltip,
    SplitContainer,
    SelectHierarchy,
    TooltipHelp,
    Message,
    Input,
    Form,
    Select,
    Button,
    ToastContext
} from '@weezevent/nacre';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import EventModel from '../../../models/eventModel';
import RateGroup from '../../../models/rateGroup';
import SubscriptionsLink from '../../../models/subscriptionsLink';
import { Session } from '../../../models';
import { useForm } from '../../../utils/validators';
import { EventSelector } from '../../widgets/components/event-selector/event-selector';
import ConfirmModal from './components/confirmModal';
import { session } from './components/formatSession';
import Styles from './subscriptionLinks.module.css';
import Subscription from '../../../models/subscription';
import SubscriptionLink from '../../../models/subscriptionLink';

const typeMapping = {
    int: 'number',
    str: 'string',
    bool: 'boolean'
};

const numberValidator = value => {
    if (!value || /^\d*$/.test(value)) {
        return false;
    }
    return isNaN(value);
};

const validateType = {
    number: numberValidator
};

function RuleCard({ form, setForm, rule, options, index, handleDelete, _options, setFieldErrors }) {
    const { t } = useTranslation();
    const [isSelector, setIsSelector] = React.useState();

    const expectedInputType = React.useMemo(() => {
        for (const [key, value] of Object.entries(_options)) {
            if (key === rule.type) {
                return typeMapping[value];
            }
        }
    }, [form, _options, rule]);

    const requiredValidation = React.useMemo(() => {
        if (!form.rules[index].type && form.rules[index].value) {
            return 'ticket.webservice.subscription-link.required-field';
        }
        return false;
    }, [form, index]);

    const requiredField = React.useMemo(() => {
        if (form.rules[index].type && (form.rules[index].value === undefined || form.rules[index].value === '')) {
            return 'ticket.webservice.subscription-link.required-field';
        }
        if (expectedInputType === 'number' && validateType['number'](form.rules[index].value)) {
            return 'ticket.webservice.subscription-link.field-error';
        }
        return false;
    }, [form, index, expectedInputType, validateType]);

    React.useEffect(() => {
        if (requiredValidation || Boolean(requiredField)) {
            setFieldErrors(true);
        } else {
            setFieldErrors(false);
        }
    }, [requiredValidation, requiredField]);

    const handleType = React.useCallback(
        value => {
            let _rules = form.rules;
            _rules[index].type = value;
            setForm('rules', _rules);
            setIsSelector(_options[value] === 'bool');
        },
        [form.rules, _options]
    );

    const handleValue = React.useCallback(
        value => {
            let _rules = form.rules;
            _rules[index].value = value;
            setForm('rules', _rules);
        },
        [form.rules]
    );

    return (
        <div className={Styles['rule-container']}>
            <Form.Field className={Styles['operator-select']}>
                <Form.Label
                    label={
                        <span>
                            {t('ticket.webservice.subscription-link.type-label')}
                            <TooltipHelp title={t('ticket.webservice.subscription-link.type-tooltip')} />
                        </span>
                    }
                />
                <Select useNew options={options} placeholder={t('ticket.webservice.subscription-link.type-placeholder')} value={rule.type} onChange={handleType} />
                {Boolean(requiredValidation) && <Form.FieldErrors errors={[t(requiredValidation)]} />}
            </Form.Field>
            <Form.Field className={Styles['operator-select']}>
                <Form.Label label={t('ticket.webservice.subscription-link.operator.label')} />
                <Select
                    useNew
                    disabled
                    options={[
                        {
                            text: t('ticket.webservice.subscription-link.operator.is'),
                            key: 'is',
                            value: 'is'
                        }
                    ]}
                    value={rule.operator}
                />
            </Form.Field>
            <Form.Field className={Styles['large-input']}>
                <Form.Label
                    label={
                        <span>
                            {t('ticket.webservice.subscription-link.value-label')}
                            <TooltipHelp title={t('ticket.webservice.subscription-link.value-tooltip')} />
                        </span>
                    }
                />
                {isSelector ? (
                    <Select
                        onChange={handleValue}
                        options={[
                            { text: 'True', value: true },
                            { text: 'False', value: false }
                        ]}
                        placeholder=""
                    />
                ) : (
                    <Input placeholder={t('ticket.webservice.subscription-link.value-placeholder')} value={rule.value} onChange={(e, { value }) => handleValue(value)} />
                )}
                {Boolean(requiredField) && <Form.FieldErrors errors={[t(requiredField)]} />}
            </Form.Field>
            <div className={Styles['delete']}>
                <Icon name="trash" margin={false} onClick={() => handleDelete(rule.id)} />
            </div>
            <div className={Styles['delete-container']} onClick={() => handleDelete(rule.id)}>
                <Icon name="trash" />
                {t('ticket.webservice.subscription-link.delete-rule')}
            </div>
        </div>
    );
}
RuleCard.propTypes = {
    form: PropTypes.object,
    setForm: PropTypes.func.isRequired,
    rule: PropTypes.object.isRequired,
    handleDelete: PropTypes.func.isRequired,
    setFieldErrors: PropTypes.func.isRequired,
    index: PropTypes.number,
    options: PropTypes.object,
    _options: PropTypes.object
};
export default function SubscriptionLinks({ organizationId, subscriptionId, linkId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [confirmModal, setConfirmModal] = React.useState(false);
    const [fieldErrors, setFieldErrors] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [_options, setOptions] = React.useState({});
    const toast = React.useContext(ToastContext);

    const [form, setForm, setFormObject] = useForm({
        name: null,
        event: null,
        session: null,
        rate_group: null,
        attendees: 0,
        rules: [],
        raw_data: {}
    });

    const handleAdd = React.useCallback(() => {
        let _rules = form.rules;
        _rules.push({ id: _rules.length ? _rules.slice(-1)[0].id + 1 : 0, type: '', value: '', operator: 'is' });
        setForm('rules', _rules);
    }, [form.rules]);

    const handleDelete = React.useCallback(
        id => {
            setForm(
                'rules',
                form.rules.filter(rule => rule.id !== id)
            );
        },
        [form.rules]
    );

    const [events, loadingEvents] = EventModel.useApiModel(
        {
            organizationId: organizationId,
            query: {
                publication_status__in: 'draft,published'
            }
        },
        [organizationId]
    );

    SubscriptionLink.useApiModel(
        {
            organizationId,
            subscriptionId,
            id: linkId,
            launch: Boolean(linkId),
            onSuccess: link => {
                let rules = [];
                let counter = 0;
                Object.keys(link.object.ticket_filter).forEach(key => {
                    rules.push({
                        id: counter,
                        type: key,
                        value: link.object.ticket_filter[key],
                        operator: 'is'
                    });
                    counter += 1;
                });
                setFormObject({ ...link.object, rules: rules });
            }
        },
        [organizationId, subscriptionId, linkId, setFormObject]
    );

    const [sessions] = Session.useApiModel(
        {
            organizationId: organizationId,
            eventId: form.event?.pk,
            launch: Boolean(organizationId && form.event?.pk && form.event?.use_sessions),
            sync: true
        },
        [organizationId, form]
    );

    const [rateGroups] = RateGroup.useApiModel(
        {
            organizationId: organizationId,
            eventId: form.event?.pk,
            launch: Boolean(organizationId && form.event?.pk),
            sync: true
        },
        [organizationId, form]
    );

    React.useEffect(() => {
        // Get all ticket filter on load.
        Subscription.get_tickets_filter({ organizationId, subscriptionId }).then(({ response }) => {
            setOptions(response);
        });
    }, [subscriptionId, organizationId]);

    const sessionOptions = React.useMemo(() => {
        return sessions?.map(option => ({ text: session(option.object.start_date, form.event), value: option.object.uuid, weez_date_id: option.weez_date_id }));
    }, [sessions]);

    const onChangeEvent = React.useCallback(
        e => {
            setFormObject(
                {
                    event: events.find(event => event.id === e),
                    session: null,
                    rate_group: null
                },
                true
            );
        },
        [events]
    );

    const options = React.useMemo(() => {
        return Object.keys(_options).map(option => {
            return {
                key: option,
                text: option,
                value: option
            };
        });
    }, [_options]);

    const _filteredOptions = React.useMemo(() => {
        if (linkId) {
            return options;
        }

        let selected = [];
        form.rules.forEach(rule => {
            rule.type && selected.push(rule.type);
        });
        return options.filter(el => !selected.includes(el.value));
    }, [form, options, linkId]);

    const onChangeSession = React.useCallback(
        e => {
            setForm(
                'session',
                sessions.find(session => session.uuid === e)
            );
        },
        [sessions]
    );

    const onChangeRateGroups = React.useCallback(
        e => {
            setForm(
                'rate_group',
                rateGroups.find(rate => rate.id === e)
            );
        },
        [rateGroups]
    );

    const rateGroupOptions = React.useMemo(() => {
        if (!rateGroups) {
            return null;
        }
        return rateGroups?.map(rate => {
            return {
                value: rate.id,
                key: rate.id,
                text: rate.name
            };
        });
    }, [rateGroups]);

    const isDisabled = React.useMemo(() => {
        return !form.name || !form.event || fieldErrors;
    }, [form, fieldErrors]);

    const onSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        let subscriptionRule = new SubscriptionsLink(form);
        subscriptionRule.ticket_filter = null;

        if (form.rules.length) {
            let obj = {};
            form.rules.forEach(rule => {
                const type = rule.type;
                obj[type] = _options[type] === 'int' ? parseInt(rule.value) : rule.value;
            });
            subscriptionRule.ticket_filter = obj;
        }

        subscriptionRule.name = form.name;
        subscriptionRule.event_id = form.event?.pk;
        subscriptionRule.date_id = form.session ? form.session.weez_date_id : null;
        subscriptionRule.category_id = form.rate_group ? form.rate_group.id : null;
        subscriptionRule.raw_data = form.raw_data;

        subscriptionRule
            .save({ organizationId: organizationId, subscriptionId: subscriptionId })
            .then(() => {
                toast.success(t('ticket.webservice.subscription-link.create-toast-success'));
                history.push(`/ticket/O${organizationId}/webservices/${subscriptionId}/subscription_links`);
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.webservice.subscription-link.create-toast-error'));
            })
            .finally(() => {
                setIsSubmitting(false);
                setConfirmModal(false);
            });
    }, [form, organizationId, subscriptionId]);

    return (
        <>
            <ConfirmModal form={form} show={confirmModal} onSubmit={onSubmit} onClose={() => setConfirmModal(false)} isSubmitting={isSubmitting} />
            <WeezHeader
                title={t('ticket.webservice.subscription-link.header-title')}
                backLink={{
                    onClick: () => history.push(`/ticket/O${organizationId}/webservices/${subscriptionId}/subscription_links`)
                }}
                rightComponent={<Button primary label={t('ticket.mailing.save')} onClick={() => setConfirmModal(true)} disabled={isDisabled} />}
            />
            <SplitContainer
                additional={
                    <Message type="tips" title={t('notifications.tips_title')} mini>
                        <Trans i18nKey="ticket.webservice.subscription-link.tips-message" />
                    </Message>
                }
            >
                <FrameCard title={t('ticket.webservice.subscription-link.card-title')} subtitle={t('ticket.webservice.subscription-link.card-subtitle')}>
                    <div className={Styles['management-name']}>
                        <Form.Field>
                            <Form.Label label={<span className={Styles['management-label']}>{t('ticket.webservice.subscription-link.management-name')}</span>} />
                            <Input
                                placeholder={t('ticket.webservice.subscription-link.management-name-placeholder')}
                                value={form.name}
                                onChange={event => setForm('name', event.target.value)}
                            />
                        </Form.Field>
                    </div>
                    {!form.rules.length ? (
                        <div className={Styles['empty-rule-form-content']}>
                            <div className={Styles['empty-title']}>{t('ticket.webservice.subscription-link.empty.title')}</div>
                            <div className={Styles['empty-info']}>{t('ticket.webservice.subscription-link.empty.info')}</div>
                            <Button primary inverted label={t('ticket.webservice.subscription-link.add-rule')} onClick={handleAdd} />
                        </div>
                    ) : (
                        <div className={Styles['rule-form-content']}>
                            <div className={Styles['creation-rule-container']}>
                                <Message type="default" title={t('ticket.webservice.subscription-link.default-message-title')}>
                                    {t('ticket.webservice.subscription-link.default-message-subtitle')}
                                </Message>
                            </div>
                            <Form>
                                {form.rules.map((rule, index) => {
                                    return (
                                        <RuleCard
                                            key={rule.id}
                                            index={index}
                                            rule={rule}
                                            form={form}
                                            setForm={setForm}
                                            options={_filteredOptions}
                                            handleDelete={handleDelete}
                                            _options={_options}
                                            setFieldErrors={setFieldErrors}
                                        />
                                    );
                                })}
                                <Button
                                    primary
                                    inverted
                                    label={t('ticket.webservice.subscription-link.add-rule')}
                                    onClick={handleAdd}
                                    disabled={options.length === form.rules.length}
                                />
                            </Form>
                        </div>
                    )}
                    <div className={Styles['send-attendees']}>
                        <div className={Styles['send-attendees-text']}>{t('ticket.webservice.subscription-link.send-attendees')}</div>
                        <div className={Styles['symbol']}>
                            <Icon name="arrow-bottom" margin={false} />
                        </div>
                    </div>
                    <div className={Styles['rule-form-content']}>
                        <div className={Styles['destination-title']}>{t('ticket.webservice.subscription-link.destination.title')}</div>
                        <div>
                            <EventSelector onChange={onChangeEvent} events={events} loadingEvents={loadingEvents} event={form.event} />
                        </div>
                        {form.event?.use_sessions && (
                            <Form.Field>
                                <Form.Label label={t('ticket.common.seances')} />
                                <SelectHierarchy
                                    placeholder={t('ticket.common.action.select')}
                                    placeholderSearch={t('ticket.common.action.search')}
                                    height="maxi"
                                    value={form.session}
                                    onChange={onChangeSession}
                                    options={sessionOptions}
                                    multi={false}
                                />
                            </Form.Field>
                        )}
                        <Form.Label
                            label={
                                <span>
                                    {t('ticket.webservice.subscription-link.destination.rate-name')}
                                    <TooltipHelp title={t('ticket.webservice.subscription-link.destination.rate-name-tooltip')} />
                                </span>
                            }
                        />
                        <Input value={`{rate_name} | ${form.name}`} disabled />
                        <OptionalSection
                            className={Styles['rule-optional-select']}
                            optionalText={t('ticket.common.labelOptional')}
                            label={t('ticket.webservice.subscription-link.destination.optional-title')}
                        >
                            <Form.Field>
                                <Form.Label label={t('ticket.rates.infos.group')} />
                                <Tooltip
                                    visible={!rateGroups?.length}
                                    triggerOnHover={true}
                                    title={t(`ticket.webservice.subscription-link.destination.${form.event ? 'no-rates-tooltip' : 'no-event-tooltip'}`)}
                                >
                                    <SelectHierarchy
                                        disabled={!rateGroups?.length}
                                        placeholder={t('ticket.common.action.select')}
                                        placeholderSearch={t('ticket.common.action.search')}
                                        height="mini"
                                        value={form.rate_group}
                                        onChange={onChangeRateGroups}
                                        options={rateGroupOptions}
                                        multi={false}
                                    />
                                </Tooltip>
                            </Form.Field>
                        </OptionalSection>
                    </div>
                </FrameCard>
            </SplitContainer>
        </>
    );
}
SubscriptionLinks.propTypes = {
    organizationId: PropTypes.string.isRequired,
    subscriptionId: PropTypes.string.isRequired
};
